import React, { useState, useEffect, useContext } from "react";
import { loadEvent } from "../../../../api/events";
import { addFavoriteEvent, removeFavoriteEvent, getFavoriteEvents } from '../../../../api/favoriteEvents';
import { UserContext } from "../../../../api/userContext";
import { formatDateOnly, formatEventRecurrenceLabel, isSameCalendarDay } from '../../../../utils/dateUtils';
import EventDetailsSkeleton from "./EventDetailsSkeleton";
import "../../../../assets/css/animate.css";
import { atcb_action } from 'add-to-calendar-button';

const EventDetailsContent = ({ eventId }) => {
  const [event, setEvent] = useState(null);
  const [isFavorite, setIsFavorite] = useState(false);
  const [loading, setLoading] = useState(true);
  const { user } = useContext(UserContext);
  const recurrenceLabel = event ? formatEventRecurrenceLabel(event) : '';

  useEffect(() => {
    const fetchEventDetails = async () => {
      try {
        console.log("Fetching event details...");
        const eventData = await loadEvent(eventId);
        console.log("Event details:", eventData);
        setEvent(eventData);
        fetchFavorites(eventId);
      } catch (error) {
        console.error("Error fetching event details:", error);
      } finally {
        setTimeout(() => setLoading(false), 1000);
      }
    };

    const fetchFavorites = async (eventId) => {
      if (user) {
        const favoriteEvents = await getFavoriteEvents(user.uid);
        if (favoriteEvents) {
          setIsFavorite(favoriteEvents.map(fav => fav.eventId).includes(eventId));
        }
      }
    };
    
    fetchEventDetails();
    
  }, [eventId, user]);
  
  const handleFavoriteClick = async () => {
    if (!user) {
      alert('Please log in to favorite events.');
      return;
    }

    if (isFavorite) {
      const { success } = await removeFavoriteEvent(user.uid, event.id);
      if (success) {
        setIsFavorite(false);
      } else {
        alert('Failed to remove favorite.');
      }
    } else {
      const { success } = await addFavoriteEvent(user.uid, event.id);
      if (success) {
        setIsFavorite(true);
      } else {
        alert('Failed to add favorite.');
      }
    }
  };

  const handleAddToCalendar = () => {
    if (!event) return;
    
    // Format dates for calendar
    const startDate = new Date(event.date);
    const endDate = event.endDate ? new Date(event.endDate) : startDate;
    
    const config = {
      name: event.title,
      description: event.description.replace(/(<([^>]+)>)/gi, ""), // Strip HTML
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0],
      startTime: startDate.toTimeString().substring(0,5),
      endTime: endDate.toTimeString().substring(0,5),
      location: `${event.location.cityName} ${event.location.postalCode || ""}`,
      options: ['Apple', 'Google', 'iCal', 'Outlook.com', 'Yahoo'],
      timeZone: "Europe/Paris",
      trigger: "click"
    };

    atcb_action(config);
  };

  return (
    <>
      <div className="col-xl-12">
        {loading ? (
          <EventDetailsSkeleton />
        ) : (
          event && (
            <div className="ed-main-wrap">
              <div className="ed-top">
                <div className="ed-thumb">
                  {/* <img src={eventUtils.getDefaultImage(event)} alt="Articles details" /> */}
                </div>
                <ul className="ed-status">
                  <li>
                    <i className="bi bi-calendar2-week" />
                    {event.endDate
                      ? (
                          isSameCalendarDay(event.date, event.endDate)
                            ? formatDateOnly(event.date)
                            : `Du ${formatDateOnly(event.date)} au ${formatDateOnly(event.endDate)}`
                        )
                      : formatDateOnly(event.date)
                    }
                  </li>
                  {recurrenceLabel && (
                    <li>
                      <i className="bi bi-arrow-repeat" />
                      {recurrenceLabel}
                    </li>
                  )}
                  <li>
                    <i className="bi bi-geo-alt" />
                    {event.location.postalCode ? `${event.location.cityName} (${event.location.postalCode})`: event.location.cityName}
                  </li>
                </ul>
                <div className="event-info row align-items-center">
                  <div className="col-lg-6 col-md-6">
                    {/* First column: Type, Speaker */}
                    <div className="single-event-info">
                      <div className="info-icon">
                        <i className="bi bi-blockquote-left" />
                      </div>
                      <div className="info-content">
                        <h5>Type d'évènement</h5>
                        <p>{event.category}</p>
                      </div>
                    </div>
                    {event.speaker && (
                      <div className="single-event-info">
                        <div className="info-icon">
                          <i className="bi bi-person" />
                        </div>
                        <div className="info-content">
                          <h5>Intervenant</h5>
                          <p>{event.speaker}</p>
                        </div>
                      </div>
                    )}
                    {event.organizer && (
                      <div className="single-event-info">
                        <div className="info-icon">
                          <i className="bi bi-building" />
                        </div>
                        <div className="info-content">
                          <h5>Organisateur</h5>
                          <p>{event.organizer}</p>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="col-lg-6 col-md-6">
                    {/* Second column: Favorite, Calendar */}
                    
                    <div className="single-event-info">
                      <div className="info-icon" style={{ cursor: 'pointer' }} onClick={handleFavoriteClick}>
                        <i className={`bi ${isFavorite ? 'bi-heart-fill' : 'bi-heart'}`} />
                      </div>
                      <div className="info-content">
                        <h5>Favori</h5>
                        <p>{isFavorite ? 'Retirer des favoris' : 'Ajouter aux favoris'}</p>
                      </div>
                    </div>
                    <div className="single-event-info">
                      <div className="info-icon" style={{ cursor: 'pointer' }} onClick={handleAddToCalendar}>
                        <i className="bi bi-calendar-plus" />
                      </div>
                      <div className="info-content">
                        <h5>Calendrier</h5>
                        <p>Ajouter à votre calendrier</p>
                      </div>
                    </div>
                  </div>
                </div>
                
              </div>
              <div className="ed-tabs-wrapper">
                <div className="tab-content" id="pills-tabContent2">
                  <div
                    className="tab-pane fade show active"
                    id="pills-details"
                    role="tabpanel"
                    aria-labelledby="pills-details-tab">
                    <div className="details-tab-content">
                      <h3 className="ed-title">
                        {event.title}
                      </h3>
                      <p dangerouslySetInnerHTML={{ __html: event.description }}></p>
                      
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )
        )}
      </div>
    </>
  );
};

export default EventDetailsContent;
